// src/services/accountService/glCategoryService.ts
import {BASE_ACCOUNT_API, fetchAPI} from '@/services/api';

export interface GlCategory {
    categoryId?: number;
    parentId?: number | null;
    level?: number; // 1=顶层, 2=二级, 3=叶子层
    code?: string;
    nameEn?: string;
    nameZh?: string;
    type?: string; // ASSET / LIABILITY / EQUITY / REVENUE / EXPENSE / ADJUSTMENT
    fsCode?: string;
    ppeCategory?: string;
    isActive?: number;
    remark?: string;
    /** Present when returned from queryTree */
    children?: GlCategory[];
}

export interface GlCategoryPage {
    code?: string;
    nameEn?: string;
    nameZh?: string;
    type?: string;
    page?: number;
    size?: number
}

const base = BASE_ACCOUNT_API + '/gl-category';

export async function getCategory(id: number) {
    return fetchAPI<GlCategory>(`${base}/${id}`);
}

export async function getCategoryTree(category: GlCategoryPage) {
    return fetchAPI<GlCategory[] | { items: GlCategory[]; total: number }>(
        `${base}/queryTree`, {
            method: 'POST',
            body: JSON.stringify(category),
        });
}

export async function listCategories(category: GlCategoryPage) {
    // 改为使用 POST Body 方式传参，以兼容后端 GLCategoryPageBO
    const url = `${base}/queryPageTree`;
    return fetchAPI<
        GlCategory[] | { items: GlCategory[]; total: number }
    >(url, {
        method: 'POST',
        body: JSON.stringify(category),
    });
}

export async function createCategory(vo: GlCategory) {
    return fetchAPI<GlCategory>(`${base}/create`, {
        method: 'POST',
        body: JSON.stringify(vo),
    });
}

export async function updateCategory(id: number, vo: GlCategory) {
    return fetchAPI<GlCategory>(`${base}/update/${id}`, {
        method: 'POST',
        body: JSON.stringify(vo),
    });
}

export async function deleteCategory(id: number) {
    return fetchAPI<void>(`${base}/delete/${id}`, {method: 'POST'});
}

export interface GlCategoryBatchUpsertItem {
    code: string;
    nameEn?: string;
    nameZh?: string;
    type?: string;
    parentCode?: string;
    level?: number;
    fsCode?: string;
    ppeCategory?: string;
    isActive?: number;
}

export interface GlCategoryBatchUpsertResult {
    created: number;
    updated: number;
    skipped: number;
    failed: number;
    messages?: string[];
}

/** POST /gl-category/batch-upsert — 按 code 批量 upsert */
export async function batchUpsertCategories(items: GlCategoryBatchUpsertItem[]) {
    return fetchAPI<GlCategoryBatchUpsertResult>(`${base}/batch-upsert`, {
        method: 'POST',
        body: JSON.stringify({items}),
    });
}

/** 全量未删除 GL Category（扁平列表，含父子） */
export async function listAllCategories(): Promise<GlCategory[]> {
    return (await fetchAPI<GlCategory[]>(`${base}/list`, {method: 'GET'})) ?? [];
}

/** 获取所有 GL Category 叶子节点（level=3），用于映射弹窗下拉选项 */
export async function fetchLeafGlCategories(): Promise<GlCategory[]> {
    const all = await listAllCategories();
    return all.filter((c) => c.level === 3 && c.code);
}

/**
 * Tiny Mode 转移目标：account_gl_category 全量叶子列（有 fsCode）。
 * 排除银行 BS-CA-CASH（允许从银行转出，不允许转到银行）。
 */
export async function fetchTransferDestinationCategories(): Promise<GlCategory[]> {
    const leaves = await fetchLeafGlCategories();
    return leaves.filter((c) =>
        !!c.fsCode
        && c.fsCode !== 'BS-CA-CASH'
        && c.isActive !== 0,
    );
}

